import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  handleLoading,
  saveSelectedAppGroupData,
  handleAppSelectedTopic,
  handleAppSelectedSubtopic,
} from "../appStore/actions/AppState/action";
import { requestGetGroupRecipient } from "../appStore/actions/ArkchatGroup/action";

export const useGroupTaskApprovalLogic = ({
  listApi,
  analyticsApi,
  filterConfig,
  currentUserData,
  setFilterActive,
  setSideBarOpen,
  setGroupActive,
  filterActive,
  groupActive,
  setSidebarLeft,
  sidebarLeft,
  setShowSidebar,
}) => {
  const dispatch = useDispatch();

  const cloneGroupList = useSelector(
    (state) => state?.arkchatgroup?.cloneGroupList,
  );

  const selectedGroupData = useSelector(
    (state) => state?.appStateData?.selectedGroupData,
  );

  // Shared Topics/Threads panel selection (Chats tab and Track Work/Performance
  // both read/write these — see Topics.jsx / Threads.jsx).
  const selectedTopicData = useSelector(
    (state) => state?.appStateData?.selectedTopicData,
  );
  const selectedSubtopicData = useSelector(
    (state) => state?.appStateData?.selectedSubtopicData,
  );
  const selectedThreadData = useSelector(
    (state) => state?.appStateData?.selectedThreadData,
  );

  const [selectedTabGroup, setSelectedTabGroup] = useState({});
  const [filterChecked, setFilterChecked] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [viewTabValue, setViewTabValue] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [selectedMember, setSelectedMember] = useState([]);
  const [buttonDrawer, setButtonDrawer] = useState(null);
  const [selectedGroupTopic, setSelectedGroupTopic] = useState([]);

  // These views follow the group selected in the sidebar, and selecting it
  // loads its recipients, topics and subtopics into the store: use those
  // (for this group only) instead of loading all three again.
  const storeRecipients = useSelector((state) => state?.arkchatgroup?.groupRecipient);
  const storeRecipientsGroupId = useSelector((state) => state?.arkchatgroup?.groupRecipientGroupId);
  const storeTopics = useSelector((state) => state?.topic?.groupTopicData?.data);
  const storeSubtopics = useSelector((state) => state?.subtopic?.groupSubtopicData);
  const tabGroupId = selectedTabGroup?.id;
  const groupTopicList = useMemo(
    () => (tabGroupId && Array.isArray(storeTopics)
      ? storeTopics.filter((t) => (t?.groupId || t?.group_id) === tabGroupId)
      : []),
    [storeTopics, tabGroupId],
  );
  const groupSubtopicList = useMemo(
    () => (tabGroupId && Array.isArray(storeSubtopics)
      ? storeSubtopics.filter((s) => (s?.groupId || s?.group_id) === tabGroupId)
      : []),
    [storeSubtopics, tabGroupId],
  );
  const recipientDetails = useMemo(() => {
    const list = tabGroupId && storeRecipientsGroupId === tabGroupId && Array.isArray(storeRecipients)
      ? storeRecipients
      : [];
    return {
      internalMember: list.filter((item) => item?.inviteType !== 2),
      externalMember: list.filter((item) => item?.inviteType === 2),
    };
  }, [storeRecipients, storeRecipientsGroupId, tabGroupId]);

  // Track/Performance no longer own their own group picker — they follow
  // whichever group is selected in the shared Groups & Contacts sidebar.
  // DMs aren't shown in that sidebar while on these tabs, but redux may still
  // point at one (e.g. left over from the Chats tab), so fall back to an
  // empty selection rather than showing stale data for the wrong group.
  useEffect(() => {
    if (selectedGroupData?.isGroup) {
      if (selectedGroupData.id !== selectedTabGroup?.id) {
        handleSelectTabGroup(selectedGroupData);
      }
    } else {
      setSelectedTabGroup({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupData?.id, selectedGroupData?.isGroup]);

  const handleSelectTabGroup = (groupData) => {
    setSelectedTabGroup(groupData);
    // Normally loaded (or loading: the request is shared) by selecting the
    // group; load it here only if the store has another group's.
    if (groupData?.id && storeRecipientsGroupId !== groupData.id) {
      dispatch(requestGetGroupRecipient(groupData.id));
    }
    const chatGroup = cloneGroupList?.find((g) => g.id === groupData?.id);
    if (chatGroup) {
      dispatch(saveSelectedAppGroupData(chatGroup));
    }
  };

  // Keep the "Topic" drawer's own highlight in sync with whichever topic is
  // selected — whether that came from the shared Topics panel or from this
  // drawer itself (handleSelectGroupTopic below writes to the same redux
  // fields), so there's a single source of truth instead of two.
  useEffect(() => {
    setSelectedGroupTopic(
      selectedSubtopicData
        ? [selectedSubtopicData]
        : selectedTopicData
          ? [selectedTopicData]
          : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicData?.id, selectedSubtopicData?.id]);

  // Builds `groupId=...` scoped with whatever topic/subtopic/thread is
  // currently selected in the shared Topics/Threads panels, plus any
  // extra params for the active sub-tab (Mine/Archive/etc).
  const buildScopedQuery = (extra = "") => {
    const parts = [`groupId=${selectedTabGroup?.id}`];
    if (selectedSubtopicData?.id) {
      parts.push(`subtopicId=${selectedSubtopicData.id}`);
    } else if (selectedTopicData?.id) {
      parts.push(`topicId=${selectedTopicData.id}`);
    }
    if (selectedThreadData?.id) {
      parts.push(`threadId=${selectedThreadData.id}`);
    }
    if (extra) {
      parts.push(extra);
    }
    return parts.join("&");
  };

  const handleCheckedFilter = (event) => {
    const { name, value } = event.target;

    if (event.target.checked) {
      const selectedType = {
        type: name,
        value: value,
      };
      setFilterChecked([...filterChecked, selectedType]);
    } else {
      const checkedData = filterChecked?.filter(
        // eslint-disable-next-line eqeqeq
        (item) => item?.value != event.target.value,
      );
      setFilterChecked(checkedData);
    }
  };

  const handleClearFilter = (page = null, lastData = null) => {
    let finalQueryString;

    if (selectedMember?.length > 0) {
      finalQueryString = buildScopedQuery(`userId=${selectedMember[0]?.userId}`);
    } else if (tabValue === 1) {
      finalQueryString = buildScopedQuery(`userId=${currentUserData?.id}`);
    } else if (tabValue === 3) {
      finalQueryString = buildScopedQuery(`is_archived=true`);
    } else {
      finalQueryString = buildScopedQuery();
    }
    dispatch(listApi(finalQueryString, page, lastData));
    setFilterChecked([]);
  };

  const handleApplyTaskFilter = (queryData, page = null, lastData = null, { silent = false } = {}) => {
    !page && !silent && dispatch(handleLoading(true));

    if (!queryData?.length) {
      handleClearFilter(page, lastData);
      return;
    }

    const params = new URLSearchParams();

    queryData.forEach((item) => {
      const config = filterConfig[item.value];
      if (!config) return;

      // Boolean filters
      if (config.type === "boolean") {
        params.set(item.value, true);
      }
      //custom key value
      if (config.type === "customValue") {
        params.set(config.customKey, config.value);
      }

      // Custom date range
      if (
        config.type === "date_range" &&
        selectedDateRange?.startDate &&
        selectedDateRange?.endDate
      ) {
        params.set(config.fromKey, selectedDateRange.startDate);
        params.set(config.toKey, selectedDateRange.endDate);
      }
    });

    const queryString = params.toString();
    let finalQueryString;

    if (selectedMember?.length > 0) {
      finalQueryString = buildScopedQuery(`userId=${selectedMember[0]?.userId}&${queryString}`);
    } else if (tabValue === 1) {
      finalQueryString = buildScopedQuery(`userId=${currentUserData?.id}&${queryString}`);
    } else if (tabValue === 3) {
      finalQueryString = buildScopedQuery(`is_archived=true&${queryString}`);
    } else {
      finalQueryString = buildScopedQuery(queryString);
    }
    dispatch(listApi(finalQueryString, page, lastData));
  };

  const handleSelectMemberCheck = (event, item) => {
    if (!event.target.checked) {
      setSelectedMember([]);
    }
    if (event.target.checked) {
      setSelectedMember([item]);
      const query = buildScopedQuery(`userId=${item?.userId}`);
      if (viewTabValue === 1) {
        dispatch(listApi(query));
      } else {
        dispatch(analyticsApi(query));
      }
    }

    handlecloseTeamlist();
  };

  // Picking a topic here (the hook's own "Topic" tab/drawer) writes to the
  // same shared redux fields as the Topics side panel, so both surfaces stay
  // in sync as one source of truth. The effect below picks up the change and
  // re-issues the fetch — no imperative fetch needed here.
  const handleSelectGroupTopic = (topic, subtopic = null) => {
    if (topic) {
      dispatch(handleAppSelectedTopic(topic));
      dispatch(handleAppSelectedSubtopic(subtopic || null));
    }

    handlecloseTopiclist();
  };
  const handleTabClick = (index) => {
    if (index === 2 && tabValue === index) {
      handleChange("", index);
    }
  };

  const handleChange = (_, newValue) => {
    if (viewTabValue === 1) {
      dispatch(handleLoading(true));
      if (newValue === 0) {
        // All = 0
        dispatch(listApi(buildScopedQuery())).then(() =>
          dispatch(handleLoading(false)),
        );
      } else if (newValue === 1) {
        // Mine = 1
        dispatch(
          listApi(buildScopedQuery(`userId=${currentUserData?.id}`)),
        ).then(() => dispatch(handleLoading(false)));
      } else if (newValue === 2) {
        // Team = 2
        handleClicktabButton("Team");
      } else if (newValue === 3) {
        // Archive = 3 (topic filtering now comes from the shared Topics
        // panel, not this tab strip — the old "Topic" tab was removed)
        dispatch(
          listApi(buildScopedQuery(`is_archived=true`)),
        ).then(() => dispatch(handleLoading(false)));
      }

      setTabValue(newValue);
      setSelectedMember([]);
      setFilterChecked([]);
      return;
    }

    if (newValue === 0) {
      // All = 0
      dispatch(analyticsApi(`groupId=${selectedTabGroup?.id}`));
    } else if (newValue === 1) {
      // Mine = 1
      dispatch(analyticsApi(`userId=${currentUserData?.id}`));
    } else if (newValue === 2) {
      // Team = 2
      handleClicktabButton("Team");
      dispatch(analyticsApi());
    } else if (newValue === 3) {
      // Topic = 3
      handleClicktabButton("Topic");
      dispatch(analyticsApi());
    } else if (newValue === 4) {
      dispatch(analyticsApi(`is_archived=true`));
    }

    setTabValue(newValue);
    setSelectedMember([]);
    setFilterChecked([]);
  };

  const handleSidebarClick = (type) => {
    if (type === "filter") {
      setFilterActive(!filterActive);
      setSideBarOpen(true);
    } else if (type === "group") {
      setGroupActive(!groupActive);
      setSideBarOpen(true);
    } else if (type === "sidebarleft") {
      setSidebarLeft(!sidebarLeft);
      if (!filterActive && !groupActive) {
        setFilterActive(!filterActive);
        setGroupActive(!groupActive);
      }
      setSideBarOpen(true);
    }
    makeFilterDeactive(type);
  };
  const handleClicktabButton = (name) => {
    setButtonDrawer({
      header: name,
      isOpen: true,
    });
  };

  const handlecloseTeamlist = (triggerFrom) => {
    if (triggerFrom === "closeButton") {
      handleChange("", 0);
    }
    setButtonDrawer({ ...buttonDrawer, isOpen: false });
  };

  const handlecloseTopiclist = (triggerFrom) => {
     if (triggerFrom === "closeButton") {
      handleChange("", 0);
    }
    setButtonDrawer({ ...buttonDrawer, isOpen: false });
  };

  const handleResetTab = () => {
    setTabValue(0);
    setSelectedMember();
    setSelectedMember([]);
  };
  const handleViewChange = (event, newValue) => {
    if (newValue === 0) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
    setTabValue(0);
    setViewTabValue(newValue);
    setSelectedMember([]);
    setFilterChecked([]);
  };

  const makeFilterDeactive = (type) => {
    if (type === "filter") {
      if (!groupActive && filterActive) {
        setSidebarLeft(false);
      }
    } else if (type === "group") {
      if (!filterActive && groupActive) {
        setSidebarLeft(false);
      }
    }
  };

  const handleAPiCall = () => {
    handleResetTab();
    if (selectedTabGroup?.id) {
      dispatch(handleLoading(true));
      dispatch(listApi(buildScopedQuery())).then(() =>
        dispatch(handleLoading(false)),
      );
      dispatch(analyticsApi(buildScopedQuery()));
    }
  };

  useEffect(() => {
    handleAPiCall();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabGroup]);

  // A topic/subtopic/thread picked in the shared panels re-runs whatever
  // view is currently active (All/Mine/Team/Archive) without resetting the
  // active tab — unlike a group switch, which is a different context.
  useEffect(() => {
    if (selectedTabGroup?.id) {
      handleClearFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicData?.id, selectedSubtopicData?.id, selectedThreadData?.id]);

  // Re-run the current query (same group, scope and filters) without the
  // loading state, for live updates.
  const refreshList = () =>
    handleApplyTaskFilter(filterChecked, null, null, { silent: true });

  return {
    refreshList,
    selectedTabGroup,
    setSelectedTabGroup,
    buttonDrawer,
    setButtonDrawer,
    selectedGroupTopic,
    setSelectedGroupTopic,
    groupTopicList,
    groupSubtopicList,
    recipientDetails,
    filterChecked,
    setFilterChecked,
    tabValue,
    setTabValue,
    viewTabValue,
    setViewTabValue,
    selectedMember,
    setSelectedMember,
    selectedDateRange,
    setSelectedDateRange,

    handleSelectTabGroup,
    handleCheckedFilter,
    handleClearFilter,
    handleApplyTaskFilter,
    handleSelectMemberCheck,
    handleSelectGroupTopic,
    handleTabClick,
    handleChange,
    handleSidebarClick,
    handleClicktabButton,
    handlecloseTeamlist,
    handlecloseTopiclist,
    handleResetTab,
    handleViewChange,
  };
};
